import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  BriefcaseIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { toast } from "sonner";
import { formatABN } from "utils/formatABN";
import { formatNumber } from "utils/formatNumber";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "U";
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

export default function ViewContactModal({ isOpen, onClose, contact, onEdit }) {
  if (!contact) return null;

  const name = contact.FullName || contact.fullName || contact.name || "Unknown";
  const phone = (contact.PhoneNumber || contact.phoneNumber || "").toString().trim();
  const email = (contact.Email || contact.email || "").toString().trim();

  const street = (contact.streetAddress || contact.StreetAddress || "").toString().trim();
  const suburb = (contact.suburb || contact.Suburb || "").toString().trim();
  const city = (contact.city || contact.City || "").toString().trim();
  const state = (contact.state || contact.State || "").toString().trim();
  const postalCode = (contact.PostalCode || "").toString().trim();
  const address = [street, suburb, city, postalCode, state].filter(Boolean).join(", ").trim();
  const abn = formatABN((contact.Abn || contact.abn || contact.ABN || "").toString().trim());
  const companyName = (contact.CompanyName || contact.companyName || contact.company_name || contact.company || contact.Company || "").toString().trim();

  const hasPhone = phone !== "";
  const hasEmail = email !== "";
  const hasAddress = address !== "";
  const hasAbn = abn !== "";
  const hasCompanyName = companyName !== "";

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white dark:bg-dark-800 p-5 text-left align-middle shadow-xl transition-all border border-gray-100 dark:border-dark-700">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title as="h3" className="text-sm font-bold text-gray-500 dark:text-dark-400">
                    Contact Details
                  </Dialog.Title>
                  <div className="flex items-center gap-1.5">
                    {onEdit && (
                      <button
                        onClick={() => {
                          onClose();
                          onEdit(contact);
                        }}
                        className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 hover:text-primary-600 transition-colors cursor-pointer"
                        title="Edit Contact"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-dark-400 transition-colors cursor-pointer"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Profile Card */}
                <div className="flex flex-col items-center text-center pb-4 mb-4 border-b border-gray-100 dark:border-dark-700">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-xl font-bold mb-3 shadow-inner">
                    {getInitials(name)}
                  </div>
                  <h4 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">
                    {name}
                  </h4>
                  <span className="text-xs text-gray-400 dark:text-dark-500">
                    Lead Contact
                  </span>
                </div>

                {/* Details Section */}
                <div className="space-y-3.5">
                  {/* Phone */}
                  {hasPhone && (
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-800 transition-all hover:bg-gray-100/50 dark:hover:bg-dark-700/30">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-1.5 rounded-md bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 shrink-0">
                          <PhoneIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            Phone Number
                          </span>
                          <a
                            href={`tel:${phone}`}
                            className="text-xs font-semibold text-gray-900 dark:text-white hover:underline truncate block"
                          >
                            {formatNumber(phone) || phone}
                          </a>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(phone, "Phone number")}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-300 transition-colors shrink-0 cursor-pointer"
                        title="Copy Phone"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Email */}
                  {hasEmail && (
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-800 transition-all hover:bg-gray-100/50 dark:hover:bg-dark-700/30">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-1.5 rounded-md bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 shrink-0">
                          <EnvelopeIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            Email Address
                          </span>
                          <a
                            href={`mailto:${email}`}
                            className="text-xs font-semibold text-gray-900 dark:text-white hover:underline truncate block"
                          >
                            {email}
                          </a>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(email, "Email address")}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-300 transition-colors shrink-0 cursor-pointer"
                        title="Copy Email"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Address */}
                  {hasAddress && (
                    <div className="flex items-start justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-800 transition-all hover:bg-gray-100/50 dark:hover:bg-dark-700/30">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="p-1.5 mt-0.5 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 shrink-0">
                          <MapPinIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            Address
                          </span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white block mt-0.5 whitespace-pre-wrap leading-relaxed">
                            {address}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(address, "Address")}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-300 transition-colors shrink-0 mt-0.5 cursor-pointer"
                        title="Copy Address"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Company Name */}
                  {hasCompanyName && (
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-800 transition-all hover:bg-gray-100/50 dark:hover:bg-dark-700/30">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-1.5 rounded-md bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400 shrink-0">
                          <BriefcaseIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            Company Name
                          </span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate block">
                            {companyName}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(companyName, "Company name")}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-300 transition-colors shrink-0 cursor-pointer"
                        title="Copy Company"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* ABN */}
                  {hasAbn && (
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-800 transition-all hover:bg-gray-100/50 dark:hover:bg-dark-700/30">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-1.5 rounded-md bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 shrink-0">
                          <DocumentTextIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            ABN
                          </span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate block">
                            {abn}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCopy(abn, "ABN")}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-dark-300 transition-colors shrink-0 cursor-pointer"
                        title="Copy ABN"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Footer Actions */}
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-dark-700 flex justify-end">
                  <button
                    onClick={onClose}
                    className="rounded-lg px-4 py-2 text-xs font-bold text-gray-600 dark:text-dark-300 bg-gray-50 hover:bg-gray-100 dark:bg-dark-900/40 dark:hover:bg-dark-700/50 transition-colors border border-gray-100 dark:border-dark-700 cursor-pointer active:scale-95"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
